import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (!marqueeItems || marqueeItems.length === 0) return;

    const copiesNeeded = this.#calculateNumberOfCopies();
    this.#addRepeatedItems(Math.max(0, copiesNeeded - this.#getCopyCount()));
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const template = this.#getTemplateItem();
    const marqueeWidth = this.offsetWidth;
    const itemWidth = template?.offsetWidth || 1;
    const count = itemWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemWidth);

    const speed = Math.sqrt(count) * (300 / speedFactor);
    return speed;
  }

  #handleResize = debounce(() => {
    const template = this.#getTemplateItem();
    const marqueeWidth = this.offsetWidth;
    const itemWidth = template?.offsetWidth || 0;

    // Skip while hidden/collapsed (common during scroll / mobile chrome)
    if (marqueeWidth === 0 || itemWidth === 0) return;

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = this.#getCopyCount();

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    this.#stripRefs(clone);

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Clone only from the template row; never duplicate refs (avoids resize miscounts).
   * @param {number} copiesToAdd
   */
  #addRepeatedItems(copiesToAdd) {
    if (copiesToAdd <= 0) return;

    const template = this.#getTemplateItem();
    const container = template?.parentElement;
    if (!template || !container) return;

    for (let i = 0; i < copiesToAdd; i++) {
      const clone = template.cloneNode(true);
      this.#stripRefs(/** @type {HTMLElement} */ (clone));
      container.appendChild(clone);
    }
  }

  /**
   * @param {number} copiesToRemove
   */
  #removeRepeatedItems(copiesToRemove) {
    if (copiesToRemove <= 0) return;

    const container = this.#getItemsContainer();
    if (!container) return;

    for (let i = 0; i < copiesToRemove; i++) {
      const items = container.querySelectorAll(':scope > .marquee__repeated-items');
      if (items.length <= 1) break;
      items[items.length - 1]?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const template = this.#getTemplateItem();
    const marqueeWidth = this.offsetWidth;
    const itemWidth = template?.offsetWidth || 1;

    return itemWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemWidth);
  }

  /** @returns {HTMLElement | null} */
  #getTemplateItem() {
    const { marqueeItems } = this.refs;
    return marqueeItems?.[0] ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getItemsContainer() {
    return this.#getTemplateItem()?.parentElement ?? null;
  }

  #getCopyCount() {
    const container = this.#getItemsContainer();
    if (!container) return 1;

    const count = container.querySelectorAll(':scope > .marquee__repeated-items').length;
    return count > 0 ? count : 1;
  }

  /**
   * @param {HTMLElement} element
   */
  #stripRefs(element) {
    element.removeAttribute('ref');
    for (const el of element.querySelectorAll('[ref]')) {
      el.removeAttribute('ref');
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
